// frontend/src/components/ui/Textarea.tsx - NEW COMPONENT
// NEW FUNCTION: Simple Textarea component for multi-line text input

import React, { forwardRef } from 'react';
import { cn } from '@/utils';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  variant?: 'default' | 'filled' | 'flushed';
  textareaSize?: 'sm' | 'md' | 'lg';
  rounded?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

const textareaVariants = {
  default: 'border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600',
  filled: 'border-transparent bg-gray-100 dark:bg-gray-700',
  flushed: 'border-0 border-b border-gray-300 rounded-none bg-transparent dark:border-gray-600',
};

const sizeVariants = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-3 py-2 text-sm',
  lg: 'px-4 py-3 text-base',
};

const roundedVariants = {
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
  label,
  error,
  helperText,
  variant = 'default',
  textareaSize = 'md',
  rounded = 'md',
  fullWidth = true,
  className,
  id,
  ...props
}, ref) => {
  const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

  const textareaElement = (
    <textarea
      ref={ref}
      id={textareaId}
      className={cn(
        'block border transition-colors duration-200 resize-vertical',
        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'placeholder:text-gray-400 dark:placeholder:text-gray-500',
        'dark:text-white',
        textareaVariants[variant],
        sizeVariants[textareaSize],
        roundedVariants[rounded],
        fullWidth && 'w-full',
        error && 'border-red-300 focus:border-red-500 focus:ring-red-500 dark:border-red-600',
        className
      )}
      {...props}
    />
  );

  return (
    <div className="space-y-1">
      {label && (
        <label
          htmlFor={textareaId}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
        </label>
      )}
      
      {textareaElement}
      
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>
      )}
      
      {helperText && !error && (
        <p className="text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';